import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { completeLesson, getCourseBySlug, getCurriculum, getLessonBlocks, getMyEnrollment, getProgress, openLesson, recordVideoProgress } from '../lib/api';
import { buildOutline, formatMinutes, neighbours, type OutlineLesson } from '../lib/progress';
import { supabase, friendlyError } from '../lib/supabase';
import { Markdown } from '../lib/markdown';
import type { Chapter, Course, Enrollment, Lesson, LessonBlock, LessonProgress } from '../lib/types';
import { Badge, Button, Callout, Icon, Lockup, ProgressBar, Spinner } from '../components/ui';
import { NotFound } from './misc';

const EMBED_HOSTS = ['www.youtube-nocookie.com', 'player.vimeo.com', 'www.arcgis.com', 'experience.arcgis.com', 'docs.google.com'];

export function CoursePlayer() {
  const { slug = '', lessonId } = useParams();
  const navigate = useNavigate();
  const { user, isStaff } = useAuth();
  const [course, setCourse] = useState<Course | null | undefined>(undefined);
  const [curr, setCurr] = useState<{ chapters: Chapter[]; lessons: Lesson[] } | null>(null);
  const [enrollment, setEnrollment] = useState<Enrollment | null>(null);
  const [progress, setProgress] = useState<LessonProgress[]>([]);
  const [blocks, setBlocks] = useState<LessonBlock[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Load course, curriculum, enrollment and progress.
  useEffect(() => {
    if (!user) return;
    let live = true;
    (async () => {
      try {
        const c = await getCourseBySlug(slug);
        if (!live) return;
        setCourse(c);
        if (!c) return;
        const [cu, en] = await Promise.all([getCurriculum(c.id), getMyEnrollment(c.id, user.id)]);
        if (!live) return;
        setCurr(cu); setEnrollment(en);
        if (en) setProgress(await getProgress(en.id));
      } catch (e) { if (live) setError(friendlyError(e)); }
    })();
    return () => { live = false; };
  }, [slug, user]);

  const outline = useMemo(() => curr && course
    ? buildOutline(curr.chapters, curr.lessons, progress, { sequential: course.sequential, enrolled: Boolean(enrollment), staff: isStaff })
    : null, [curr, course, progress, enrollment, isStaff]);

  // Pick the lesson: the URL's, else where the learner left off, else the next incomplete one.
  const current: OutlineLesson | null = useMemo(() => {
    if (!outline) return null;
    const byId = (id?: string | null) => outline.ordered.find((l) => l.id === id) ?? null;
    return byId(lessonId) ?? byId(enrollment?.last_lesson_id) ?? outline.nextLesson ?? outline.ordered[0] ?? null;
  }, [outline, lessonId, enrollment]);

  useEffect(() => {
    if (current && !lessonId && course) navigate(`/learn/${course.slug}/${current.id}`, { replace: true });
  }, [current, lessonId, course, navigate]);

  // Open the lesson (records access + resume point) and load its content.
  useEffect(() => {
    if (!current || current.locked) { setBlocks(null); return; }
    let live = true;
    setBlocks(null); setNotice(null);
    (async () => {
      try {
        if (enrollment) {
          const row = await openLesson(current.id);
          if (live) setProgress((p) => [...p.filter((x) => x.lesson_id !== row.lesson_id), row]);
        }
        const b = await getLessonBlocks(current.id);
        if (live) setBlocks(b);
      } catch (e) { if (live) setError(friendlyError(e)); }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id, current?.locked, enrollment?.id]);

  const myProgress = current ? progress.find((p) => p.lesson_id === current.id) : undefined;
  const updateRow = useCallback((row: LessonProgress) => setProgress((p) => [...p.filter((x) => x.lesson_id !== row.lesson_id), row]), []);

  if (!user) return null;
  if (error) return <PlayerError message={error} />;
  if (course === undefined || !outline) return <Spinner label="Loading course…" />;
  if (course === null) return <NotFound message="That course isn't available." />;
  if (!enrollment && !isStaff) {
    return (
      <PlayerError message="You're not enrolled in this course yet.">
        <Link to={`/courses/${course.slug}`}>See course details</Link>
      </PlayerError>
    );
  }

  const { prev, next, index } = current ? neighbours(outline.ordered, current.id) : { prev: null, next: null, index: -1 };
  const chapter = current ? outline.chapters.find((c) => c.id === current.chapter_id) : undefined;
  const canComplete = Boolean(enrollment && current && myProgress?.status !== 'completed' && (
    current.completion_rule === 'manual' || (current.completion_rule === 'video_pct' && (myProgress?.video_pct ?? 0) >= current.video_threshold_pct)));

  async function markComplete() {
    if (!current) return;
    setBusy(true); setNotice(null);
    try {
      updateRow(await completeLesson(current.id));
      setNotice('Lesson complete. Progress saved.');
    } catch (e) { setNotice(friendlyError(e)); }
    setBusy(false);
  }

  return (
    <>
      <a href="#lesson" className="skip-link">Skip to lesson</a>
      <header className="app-header">
        <div className="app-header-inner" style={{ padding: '10px 24px' }}>
          <Lockup markOnly size={36} />
          <Link to="/" className="row small" style={{ gap: 6, textDecoration: 'none' }}><Icon name="chevronLeft" />Dashboard</Link>
          <span style={{ flex: '1 1 280px', fontWeight: 600, color: 'var(--ink-strong)', fontSize: 15 }}>{course.title}</span>
          <div style={{ flex: '0 1 260px', minWidth: 200 }}>
            <ProgressBar value={outline.percent} size="sm"
              label={`${outline.ordered.filter((l) => l.is_required && l.status === 'done').length} of ${outline.ordered.filter((l) => l.is_required).length} lessons`} />
          </div>
        </div>
      </header>

      <div className="player">
        <nav aria-label="Course curriculum" className="player-nav">
          <div style={{ padding: '20px 20px 12px' }} className="stack">
            <span className="overline">Curriculum</span>
            <span className="small muted" style={{ marginTop: -12 }}>{outline.remainingMinutes ? `About ${formatMinutes(outline.remainingMinutes)} remaining` : 'All lessons complete'}</span>
          </div>
          {outline.chapters.map((ch) => (
            <details key={ch.id} open={ch.id === current?.chapter_id}>
              <summary>
                <span>{ch.title}</span>
                <span className="small" style={{ fontWeight: ch.completed === ch.required && ch.required ? 600 : 400, color: ch.completed === ch.required && ch.required ? 'var(--accent)' : 'var(--ink-muted)', whiteSpace: 'nowrap' }}>
                  {ch.completed === ch.required && ch.required ? 'Done' : `${ch.completed} of ${ch.required}`}
                </span>
              </summary>
              <ul className="bl-lessons" style={{ paddingBottom: 8 }}>
                {ch.lessons.map((l) => <LessonRow key={l.id} lesson={l} slug={course.slug} current={l.id === current?.id} />)}
              </ul>
            </details>
          ))}
        </nav>

        <main className="player-main" id="lesson">
          {!current ? (
            <div className="player-article"><div className="empty">This course has no lessons yet.</div></div>
          ) : (
            <article className="player-article">
              <header>
                <span className="overline" style={{ display: 'block', marginBottom: 8 }}>
                  {chapter?.title} · Lesson {chapter ? chapter.lessons.findIndex((l) => l.id === current.id) + 1 : index + 1} of {chapter?.lessons.length}
                </span>
                <h1 style={{ margin: 0, fontSize: 36, lineHeight: '44px', fontWeight: 400, color: 'var(--brand)' }}>{current.title}</h1>
                <hr className="bl-title-rule" aria-hidden="true" />
                {isStaff && !enrollment && <p className="small muted">Staff preview: progress isn't recorded.</p>}
              </header>

              {current.locked ? (
                <Callout variant="note">This lesson unlocks after you complete the previous one{prev ? <>: <Link to={`/learn/${course.slug}/${prev.id}`}>{prev.title}</Link></> : ''}.</Callout>
              ) : !blocks ? <Spinner label="Loading lesson…" /> : blocks.length === 0 ? (
                <div className="empty">Content for this lesson is coming soon.</div>
              ) : (
                blocks.map((b) => <Block key={b.id} block={b} lesson={current} progress={myProgress} enrolled={Boolean(enrollment)} onProgress={updateRow} />)
              )}

              {notice && <Callout variant={/saved/.test(notice) ? 'note' : 'error'} role="status">{notice}</Callout>}

              <footer className="player-footer">
                {prev ? <Link className="bl-btn bl-btn-secondary" to={`/learn/${course.slug}/${prev.id}`}>Previous lesson</Link> : <span />}
                <div className="row">
                  {myProgress?.status === 'completed'
                    ? <Badge tone="success">Completed</Badge>
                    : enrollment && <Button variant="ghost" icon="check" onClick={markComplete} disabled={!canComplete} busy={busy}>Mark as complete</Button>}
                  {next && !next.locked && <Link className="bl-btn bl-btn-primary" to={`/learn/${course.slug}/${next.id}`}>Next lesson <Icon name="chevronRight" /></Link>}
                  {next && next.locked && <Button disabled iconAfter="lock">Next lesson</Button>}
                </div>
              </footer>
              {enrollment && myProgress?.status !== 'completed' && current.completion_rule === 'video_pct' && (
                <p className="small muted" style={{ margin: '-12px 0 0', textAlign: 'right' }}>Completes automatically after {current.video_threshold_pct}% of the video is watched.</p>
              )}
              {enrollment && myProgress?.status !== 'completed' && current.completion_rule === 'activity_passed' && (
                <p className="small muted" style={{ margin: '-12px 0 0', textAlign: 'right' }}>Completes when you pass this lesson's activity.</p>
              )}
            </article>
          )}
        </main>
      </div>
    </>
  );
}

function LessonRow({ lesson, slug, current }: { lesson: OutlineLesson; slug: string; current: boolean }) {
  const dot = (
    <span className="bl-lesson-dot">
      {lesson.status === 'done' ? <Icon name="check" strokeWidth={3} label="Completed" /> : lesson.locked ? <Icon name="lock" label="Locked" /> : null}
    </span>
  );
  const cls = `bl-lesson bl-lesson-${lesson.locked ? 'locked' : lesson.status === 'done' ? 'done' : 'todo'}`;
  const meta = <span className="bl-lesson-meta">{lesson.est_minutes ? `${lesson.est_minutes} min` : ''}</span>;
  return (
    <li>
      {lesson.locked
        ? <span className={cls} aria-disabled="true">{dot}<span>{lesson.title}</span>{meta}</span>
        : <Link className={cls} to={`/learn/${slug}/${lesson.id}`} aria-current={current ? 'step' : undefined} style={{ textDecoration: 'none' }}>{dot}<span>{lesson.title}</span>{meta}</Link>}
    </li>
  );
}

function PlayerError({ message, children }: { message: string; children?: React.ReactNode }) {
  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <Lockup size={40} />
        <Callout variant="error">{message}</Callout>
        <div className="row">{children}<Link to="/">Back to dashboard</Link></div>
      </div>
    </div>
  );
}

function Block({ block, lesson, progress, enrolled, onProgress }:
  { block: LessonBlock; lesson: OutlineLesson; progress?: LessonProgress; enrolled: boolean; onProgress: (r: LessonProgress) => void }) {
  const c = block.config as Record<string, string | number | undefined>;
  switch (block.type) {
    case 'text':
      return <Markdown source={String(c.markdown ?? '')} />;
    case 'video':
      return <VideoBlock config={c} lesson={lesson} progress={progress} enrolled={enrolled} onProgress={onProgress} />;
    case 'image':
      return (
        <figure style={{ margin: 0 }}>
          <img src={String(c.src ?? '')} alt={String(c.alt ?? '')} style={{ borderRadius: 'var(--radius-md)', width: '100%' }} />
          {c.caption && <figcaption className="small muted" style={{ marginTop: 8 }}>{c.caption}</figcaption>}
        </figure>
      );
    case 'audio':
      return <audio controls preload="metadata" src={String(c.src ?? '')} style={{ width: '100%' }}>Your browser can't play this audio.</audio>;
    case 'pdf':
    case 'file':
    case 'slides':
      return <FileBlock config={c} kind={block.type} />;
    case 'embed': {
      let host = '';
      try { host = new URL(String(c.url ?? '')).host; } catch { /* invalid URL */ }
      if (!EMBED_HOSTS.includes(host)) return <Callout variant="note">This embedded resource isn't from an approved source.</Callout>;
      return <iframe title={String(c.title ?? 'Embedded resource')} src={String(c.url)} className="video-frame" sandbox="allow-scripts allow-same-origin allow-popups" loading="lazy" referrerPolicy="no-referrer" />;
    }
    case 'activity':
      return <Callout variant="implication" title="Interactive activity:">This lesson includes an interactive exercise. The activity player is coming in the next release.</Callout>;
    case 'survey':
    case 'assignment':
      return <Callout variant="note">{block.type === 'survey' ? 'A short feedback survey' : 'An assignment upload'} will appear here in a coming release.</Callout>;
    default:
      return null;
  }
}

function FileBlock({ config, kind }: { config: Record<string, string | number | undefined>; kind: string }) {
  const [err, setErr] = useState<string | null>(null);
  const label = String(config.label ?? (kind === 'slides' ? 'Slides' : 'Download'));
  async function open() {
    setErr(null);
    if (config.url) { window.open(String(config.url), '_blank', 'noopener'); return; }
    // Private course files are served through short-lived signed URLs.
    const { data, error } = await supabase.storage.from('course-files').createSignedUrl(String(config.storage_path ?? ''), 300);
    if (error || !data) setErr('That file isn’t available right now.');
    else window.open(data.signedUrl, '_blank', 'noopener');
  }
  return (
    <div>
      <button type="button" className="resource" onClick={open} style={{ width: '100%', background: 'transparent', font: 'inherit', cursor: 'pointer', textAlign: 'left' }}>
        <Icon name="file" /><span style={{ flexGrow: 1 }}>{label}</span><span className="small" style={{ color: 'var(--brand)', fontWeight: 600 }}>Open</span>
      </button>
      {err && <p className="small" style={{ color: 'var(--danger)' }}>{err}</p>}
    </div>
  );
}

function VideoBlock({ config, lesson, progress, enrolled, onProgress }:
  { config: Record<string, string | number | undefined>; lesson: OutlineLesson; progress?: LessonProgress; enrolled: boolean; onProgress: (r: LessonProgress) => void }) {
  const ref = useRef<HTMLVideoElement>(null);
  const lastSent = useRef(0);
  const resumed = useRef(false);

  const send = useCallback(async () => {
    const v = ref.current;
    if (!v || !enrolled || !v.duration || !Number.isFinite(v.duration)) return;
    lastSent.current = Date.now();
    try { onProgress(await recordVideoProgress(lesson.id, Math.floor(v.currentTime), Math.round(v.duration))); } catch { /* retried on next heartbeat */ }
  }, [enrolled, lesson.id, onProgress]);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onTime = () => { if (!v.paused && Date.now() - lastSent.current > 15000) void send(); };
    const onMeta = () => {
      if (!resumed.current && progress?.video_position_s && progress.video_position_s < v.duration - 5) v.currentTime = Number(progress.video_position_s);
      resumed.current = true;
    };
    v.addEventListener('timeupdate', onTime);
    v.addEventListener('pause', send);
    v.addEventListener('ended', send);
    v.addEventListener('loadedmetadata', onMeta);
    const onHide = () => { if (document.visibilityState === 'hidden') void send(); };
    document.addEventListener('visibilitychange', onHide);
    return () => {
      v.removeEventListener('timeupdate', onTime);
      v.removeEventListener('pause', send);
      v.removeEventListener('ended', send);
      v.removeEventListener('loadedmetadata', onMeta);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, [send, progress?.video_position_s]);

  if (!config.src) {
    return <Callout variant="note">Video for this lesson is being prepared.</Callout>;
  }
  return (
    <figure style={{ margin: 0 }}>
      <video ref={ref} className="video-frame" controls preload="metadata" src={String(config.src)}>
        {config.captions && <track kind="captions" src={String(config.captions)} srcLang="en" label="English" default />}
      </video>
      <figcaption className="small muted" style={{ marginTop: 8 }}>
        {progress?.video_position_s ? `Resumes at ${Math.floor(Number(progress.video_position_s) / 60)}:${String(Math.floor(Number(progress.video_position_s) % 60)).padStart(2, '0')}. ` : ''}
        {lesson.completion_rule === 'video_pct' ? `Watched ${Math.round(Number(progress?.video_pct ?? 0))}% · ${lesson.video_threshold_pct}% needed to complete.` : ''}
      </figcaption>
    </figure>
  );
}
